<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CustomSlaPoliciesAPI from 'dashboard/api/customSlaPolicies';
import Input from 'dashboard/components-next/input/Input.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const store = useStore();

const policies = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const editingPolicy = ref(null);

const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

const inboxOptions = computed(() =>
  (inboxes.value || []).map(i => ({ id: i.id, name: i.name }))
);
const teamOptions = computed(() =>
  (teams.value || []).map(t => ({ id: t.id, name: t.name }))
);

const form = ref(defaultForm());

function defaultForm() {
  return {
    name: '',
    description: '',
    first_response_time_value: 5,
    first_response_time_unit: 'minutes',
    first_response_trigger_text: '',
    next_response_time_value: 10,
    next_response_time_unit: 'minutes',
    resolution_time_value: 30,
    resolution_time_unit: 'minutes',
    only_during_business_hours: false,
    inbox_ids: [],
    team_ids: [],
    criteria: [],
  };
}

const unitOptions = [
  { value: 'seconds', label: 'Segundos' },
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
];

function toSeconds(value, unit) {
  const factors = { seconds: 1, minutes: 60, hours: 3600 };
  return (value || 0) * (factors[unit] || 60);
}

function fromSeconds(seconds) {
  if (!seconds || seconds === 0) return { value: 0, unit: 'minutes' };
  if (seconds >= 3600 && seconds % 3600 === 0) return { value: seconds / 3600, unit: 'hours' };
  if (seconds >= 60 && seconds % 60 === 0) return { value: seconds / 60, unit: 'minutes' };
  return { value: seconds, unit: 'seconds' };
}

function formatTime(seconds) {
  if (!seconds) return '—';
  if (seconds >= 3600) return `${seconds / 3600}h`;
  if (seconds >= 60) return `${seconds / 60}min`;
  return `${seconds}s`;
}

function addCriterion() {
  form.value.criteria.push({
    id: null, name: '', trigger_text: '',
    time_limit_value: 5, time_limit_unit: 'minutes', _destroy: false,
  });
}

function removeCriterion(index) {
  const c = form.value.criteria[index];
  if (c.id) c._destroy = true;
  else form.value.criteria.splice(index, 1);
}

// MultiSelect uses {id, name} objects as model
const selectedInboxes = computed({
  get: () => form.value.inbox_ids.map(id => ({ id, name: inboxOptions.value.find(i => i.id === id)?.name || '' })),
  set: val => { form.value.inbox_ids = val.map(v => v.id); },
});
const selectedTeams = computed({
  get: () => form.value.team_ids.map(id => ({ id, name: teamOptions.value.find(t => t.id === id)?.name || '' })),
  set: val => { form.value.team_ids = val.map(v => v.id); },
});

async function loadPolicies() {
  isLoading.value = true;
  try {
    const res = await CustomSlaPoliciesAPI.get();
    policies.value = res.data || [];
  } catch { policies.value = []; }
  isLoading.value = false;
}

function openCreate() {
  editingPolicy.value = null;
  form.value = defaultForm();
  showModal.value = true;
}

function openEdit(policy) {
  editingPolicy.value = policy;
  const frt = fromSeconds(policy.first_response_time_seconds);
  const nrt = fromSeconds(policy.next_response_time_seconds);
  const rst = fromSeconds(policy.resolution_time_seconds);
  form.value = {
    name: policy.name,
    description: policy.description || '',
    first_response_time_value: frt.value,
    first_response_time_unit: frt.unit,
    first_response_trigger_text: policy.first_response_trigger_text || '',
    next_response_time_value: nrt.value,
    next_response_time_unit: nrt.unit,
    resolution_time_value: rst.value,
    resolution_time_unit: rst.unit,
    only_during_business_hours: policy.only_during_business_hours || false,
    inbox_ids: (policy.inboxes || []).map(i => i.id),
    team_ids: (policy.teams || []).map(t => t.id),
    criteria: (policy.custom_sla_criteria || []).map(c => {
      const ct = fromSeconds(c.time_limit_seconds);
      return { id: c.id, name: c.name, trigger_text: c.trigger_text, time_limit_value: ct.value, time_limit_unit: ct.unit, _destroy: false };
    }),
  };
  showModal.value = true;
}

async function submitForm() {
  const criteriaAttrs = form.value.criteria.map(c => ({
    id: c.id || undefined,
    name: c.name, trigger_text: c.trigger_text,
    time_limit_seconds: toSeconds(c.time_limit_value, c.time_limit_unit),
    _destroy: c._destroy || false,
  }));
  const payload = {
    custom_sla_policy: {
      name: form.value.name,
      description: form.value.description,
      first_response_time_seconds: toSeconds(form.value.first_response_time_value, form.value.first_response_time_unit),
      first_response_trigger_text: form.value.first_response_trigger_text,
      next_response_time_seconds: toSeconds(form.value.next_response_time_value, form.value.next_response_time_unit),
      resolution_time_seconds: toSeconds(form.value.resolution_time_value, form.value.resolution_time_unit),
      only_during_business_hours: form.value.only_during_business_hours,
      inbox_ids: form.value.inbox_ids,
      team_ids: form.value.team_ids,
      custom_sla_criteria_attributes: criteriaAttrs,
    },
  };
  try {
    if (editingPolicy.value) {
      await CustomSlaPoliciesAPI.update(editingPolicy.value.id, payload);
      useAlert('SLA atualizado com sucesso');
    } else {
      await CustomSlaPoliciesAPI.create(payload);
      useAlert('SLA criado com sucesso');
    }
    showModal.value = false;
    loadPolicies();
  } catch (e) {
    useAlert(e?.response?.data?.message || 'Erro ao salvar SLA');
  }
}

async function deletePolicy(policy) {
  if (!confirm(`Deseja excluir o SLA "${policy.name}"?`)) return;
  try {
    await CustomSlaPoliciesAPI.delete(policy.id);
    useAlert('SLA excluído com sucesso');
    loadPolicies();
  } catch { useAlert('Erro ao excluir SLA'); }
}

onMounted(() => {
  loadPolicies();
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto p-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">SLA de Atendimento</h1>
        <p class="text-sm text-n-slate-11 mt-1">Configure políticas de tempo de resposta por inbox e time.</p>
      </div>
      <button class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:opacity-90 transition-opacity" @click="openCreate">
        <span class="i-lucide-plus size-4" />
        Nova Política
      </button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <span class="i-lucide-loader-2 animate-spin size-7 text-n-brand" />
    </div>

    <div v-else-if="policies.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
      <span class="i-lucide-timer size-16 text-n-slate-8 mb-4" />
      <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Nenhuma política de SLA</h3>
      <p class="text-sm text-n-slate-11 mb-4 max-w-sm">Crie políticas para monitorar tempos de resposta dos atendimentos.</p>
      <button class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:opacity-90" @click="openCreate">
        <span class="i-lucide-plus size-4" />
        Criar primeira política
      </button>
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="policy in policies"
        :key="policy.id"
        class="p-4 rounded-xl border border-n-weak bg-n-background hover:bg-n-alpha-1 transition-colors"
      >
        <div class="flex items-start justify-between mb-2">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="i-lucide-timer size-4 text-n-brand flex-shrink-0" />
            <span class="font-semibold text-sm text-n-slate-12">{{ policy.name }}</span>
            <span v-if="policy.only_during_business_hours" class="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <span class="i-lucide-building-2 size-2.5" />
              Horário comercial
            </span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button class="p-2 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors" @click="openEdit(policy)">
              <span class="i-lucide-pencil size-4" />
            </button>
            <button class="p-2 rounded-lg hover:bg-red-50 text-n-slate-11 hover:text-red-500 dark:hover:bg-red-900/20 transition-colors" @click="deletePolicy(policy)">
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
        <p v-if="policy.description" class="text-xs text-n-slate-11 mb-3">{{ policy.description }}</p>
        <div class="flex flex-wrap gap-3 text-xs text-n-slate-11 mb-3">
          <span class="flex items-center gap-1">
            <span class="i-lucide-message-circle size-3 text-blue-500" />
            1ª Resposta: <strong class="text-n-slate-12 ml-0.5">{{ formatTime(policy.first_response_time_seconds) }}</strong>
          </span>
          <span v-if="policy.first_response_trigger_text" class="flex items-center gap-1">
            <span class="i-lucide-text-cursor-input size-3 text-purple-500" />
            Gatilho: <strong class="text-n-slate-12 ml-0.5">"{{ policy.first_response_trigger_text }}"</strong>
          </span>
          <span class="flex items-center gap-1">
            <span class="i-lucide-clock size-3 text-orange-500" />
            Próx. Resposta: <strong class="text-n-slate-12 ml-0.5">{{ formatTime(policy.next_response_time_seconds) }}</strong>
          </span>
          <span v-if="policy.resolution_time_seconds" class="flex items-center gap-1">
            <span class="i-lucide-check-circle size-3 text-green-500" />
            Resolução: <strong class="text-n-slate-12 ml-0.5">{{ formatTime(policy.resolution_time_seconds) }}</strong>
          </span>
        </div>
        <div v-if="(policy.inboxes || []).length || (policy.teams || []).length" class="flex flex-wrap gap-1.5 mb-2">
          <span v-for="inbox in (policy.inboxes || [])" :key="'i-' + inbox.id" class="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
            <span class="i-lucide-inbox size-2.5" /> {{ inbox.name }}
          </span>
          <span v-for="team in (policy.teams || [])" :key="'t-' + team.id" class="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
            <span class="i-lucide-users size-2.5" /> {{ team.name }}
          </span>
        </div>
        <div v-if="policy.custom_sla_criteria && policy.custom_sla_criteria.length > 0" class="mt-2 pt-2 border-t border-n-weak">
          <span class="text-[10px] uppercase tracking-wider font-medium text-n-slate-10 mb-1.5 block">Critérios</span>
          <div class="flex flex-wrap gap-1.5">
            <span v-for="c in policy.custom_sla_criteria" :key="c.id" class="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400">
              <span class="i-lucide-target size-3" /> {{ c.name }} · "{{ c.trigger_text }}" · {{ formatTime(c.time_limit_seconds) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="sla-modal">
        <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div class="bg-n-background rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden border border-n-weak" @click.stop>
            <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ editingPolicy ? 'Editar Política de SLA' : 'Nova Política de SLA' }}
              </h2>
              <button class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-11" @click="showModal = false">
                <span class="i-lucide-x size-5" />
              </button>
            </div>

            <div class="px-6 py-5 max-h-[75vh] overflow-y-auto">

              <div class="space-y-4 mb-6">
                <Input v-model="form.name" label="Nome da política" placeholder="Ex: SLA Premium" />
                <div>
                  <label class="mb-0.5 text-heading-3 text-n-slate-12 block">Descrição</label>
                  <textarea
                    v-model="form.description"
                    rows="2"
                    class="block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand h-16 px-3 py-2.5 resize-none transition-all duration-200"
                    placeholder="Descreva o propósito desta política..."
                  />
                </div>
              </div>

              <div class="mb-6">
                <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3">
                  <span class="i-lucide-filter size-3.5" />
                  Escopo de Aplicação
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Caixas de Entrada</label>
                    <MultiSelect v-model="selectedInboxes" :options="inboxOptions" :max-chips="2" />
                  </div>
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Times</label>
                    <MultiSelect v-model="selectedTeams" :options="teamOptions" :max-chips="2" />
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3">
                  <span class="i-lucide-clock size-3.5" />
                  Tempos de Resposta
                </div>

                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Tempo de 1ª Resposta</label>
                    <div class="flex items-center gap-2">
                      <Input v-model="form.first_response_time_value" type="number" placeholder="5" class="flex-1" />
                      <Select v-model="form.first_response_time_unit" :options="unitOptions" />
                    </div>
                  </div>
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Gatilho da 1ª Resposta</label>
                    <Input v-model="form.first_response_trigger_text" placeholder="Ex: olá, seja bem-vindo" message="Frase que conta como 1ª resposta." />
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Próximo Tempo de Resposta</label>
                    <div class="flex items-center gap-2">
                      <Input v-model="form.next_response_time_value" type="number" placeholder="10" class="flex-1" />
                      <Select v-model="form.next_response_time_unit" :options="unitOptions" />
                    </div>
                    <p class="text-[11px] text-n-slate-10 mt-1">Intervalo ideal entre cada mensagem.</p>
                  </div>
                  <div>
                    <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Tempo de Resolução</label>
                    <div class="flex items-center gap-2">
                      <Input v-model="form.resolution_time_value" type="number" placeholder="30" class="flex-1" />
                      <Select v-model="form.resolution_time_unit" :options="unitOptions" />
                    </div>
                    <p class="text-[11px] text-n-slate-10 mt-1">Tempo máximo da abertura até resolver.</p>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">
                    <span class="i-lucide-target size-3.5" />
                    Critérios de Análise
                  </div>
                  <button class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-brand rounded-lg border border-n-brand/20 hover:bg-n-brand/5 transition-colors" @click="addCriterion">
                    <span class="i-lucide-plus size-3" />
                    Adicionar
                  </button>
                </div>

                <div v-if="form.criteria.filter(c => !c._destroy).length === 0" class="flex flex-col items-center py-6 border border-dashed border-n-weak rounded-xl bg-n-alpha-black2">
                  <span class="i-lucide-target size-8 text-n-slate-8 mb-2" />
                  <span class="text-xs text-n-slate-10">Nenhum critério adicional configurado.</span>
                </div>

                <div v-for="(c, idx) in form.criteria" :key="idx" class="mb-2 last:mb-0">
                  <div v-if="!c._destroy" class="p-3 rounded-xl bg-n-alpha-black2 border border-n-weak">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">
                        Critério {{ form.criteria.filter((x, i) => !x._destroy && i <= idx).length }}
                      </span>
                      <button class="p-1 rounded-lg hover:bg-red-50 text-n-slate-10 hover:text-red-500 dark:hover:bg-red-900/20 transition-colors" @click="removeCriterion(idx)">
                        <span class="i-lucide-trash-2 size-3.5" />
                      </button>
                    </div>
                    <div class="grid grid-cols-3 gap-3">
                      <Input v-model="c.name" size="sm" placeholder="Nome (ex: Orçamento)" />
                      <Input v-model="c.trigger_text" size="sm" placeholder="Frase gatilho" />
                      <div class="flex items-center gap-2">
                        <Input v-model="c.time_limit_value" type="number" size="sm" placeholder="5" class="flex-1" />
                        <Select v-model="c.time_limit_unit" :options="unitOptions" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-alpha-black2">
                <div class="flex items-center gap-3">
                  <div class="size-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <span class="i-lucide-building-2 size-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <span class="text-sm font-medium text-n-slate-12 block">Apenas horário comercial</span>
                    <p class="text-[11px] text-n-slate-10 mt-0.5">Contabilizar tempos somente dentro do horário comercial.</p>
                  </div>
                </div>
                <label class="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                  <input v-model="form.only_during_business_hours" type="checkbox" class="sr-only peer" />
                  <div class="w-10 h-[22px] bg-n-slate-7 peer-focus:ring-2 peer-focus:ring-n-brand/30 rounded-full peer peer-checked:after:translate-x-[18px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-[18px] after:w-[18px] after:transition-all peer-checked:bg-n-brand" />
                </label>
              </div>
            </div>

            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak">
              <button class="px-4 py-2 text-sm font-medium text-n-slate-11 rounded-lg hover:bg-n-alpha-2 transition-colors" @click="showModal = false">
                Cancelar
              </button>
              <button
                class="px-5 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                :disabled="!form.name.trim()"
                @click="submitForm"
              >
                {{ editingPolicy ? 'Salvar' : 'Criar' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.sla-modal-enter-active, .sla-modal-leave-active { transition: opacity 0.2s ease; }
.sla-modal-enter-from, .sla-modal-leave-to { opacity: 0; }
</style>
